import { memo, useState, useCallback, useEffect, useRef } from 'react'
import type { Diamond, SearchFilters } from '../api/types'
import { StoneImage } from './StoneImage'
import { formatPrice, formatPerCarat, formatCarat, displayStockId } from '../lib/format'
import { stoneKey } from '../lib/stoneKey'
import { feedback } from '../lib/feedback'
import { FinishStrip, formatMeasurements, SpecSheet } from './StoneSpecs'
import { CertLink } from './CertLink'
import { SupplierName, SaveStoneButton } from './StoneActions'
import { PriceMark, PeerExplainer, usePeer } from './StonePeer'

type Sort = NonNullable<SearchFilters['sortBy']>

interface Props {
  stones: Diamond[]
  onOpen?: (s: Diamond) => void
  blurPrice?: boolean
  sort: Sort
  onSort: (s: Sort) => void
  /** Share selection (#56): when provided, a leading checkbox column appears.
   *  Keyed by stoneKey(s). Toggling a row calls onToggleSelect(stone). */
  selectedKeys?: Set<string>
  onToggleSelect?: (s: Diamond) => void
  /** Toggle every stone on the current page on/off in one shot. */
  onToggleAll?: (stones: Diamond[]) => void
  /** Where an anonymous viewer's "save stone" press should go. Passed in
   *  rather than resolved here because this component is rendered outside a
   *  Router in its own tests, and `useNavigate` throws there. */
  onRequireSignIn?: () => void
}

/**
 * Dense catalog table — the trading tool. Calm density (spec #5):
 *  - hairline rows (border-line), NO zebra
 *  - sticky header with a line-strong bottom rule, paper bg
 *  - hover/selected = gold-050 tint + 2px gold left-border
 *  - numeric cols right-aligned tabular-nums; stock id mono; text left
 *  - column heads = navy-300 label style; sortable get a gold caret
 *    ONLY on the active sort (gold is a seasoning)
 *  - whole tbody fades on data change (no per-row mount thrash)
 *
 * ── THE STICKY IDENTITY GUTTER (2026-08-11) ───────────────────────────────
 * This table is `min-w-[1280px]` inside an `overflow-x-auto`, and only the
 * ordinal cell used to be sticky. So the moment you scrolled right to read
 * $/ct — the column the trade actually compares on — the stock id, the
 * thumbnail, the shape and the carat had all left the screen, and the finish
 * strip's entire premise (scan quality down the page pre-attentively) died
 * with them. The selection checkbox, the ordinal and the stock id now pin as
 * one gutter, so the row never stops saying which stone it is.
 */
export const DiamondTable = memo(function DiamondTable({ stones, onOpen, blurPrice, sort, onSort, selectedKeys, onToggleSelect, onToggleAll, onRequireSignIn }: Props) {
  const selectable = !!onToggleSelect
  const allSelected = selectable && stones.length > 0 && stones.every((s) => selectedKeys?.has(stoneKey(s)))
  // Key the tbody on the current sort + first/last stone so a data swap
  // triggers ONE calm fade of the whole body, not a per-row animation.
  const bodyKey = `${sort}:${stones.length}:${stones[0] ? stoneKey(stones[0]) : ''}`
  // Cells the expansion row must span. Counted rather than hardcoded so a
  // future column cannot silently leave the detail panel one cell short.
  //
  // Supplier and Actions are now UNCONDITIONAL (they used to appear and
  // disappear with `!blurPrice` / `askaServices`), so the count no longer
  // depends on who is looking — which also means the table's column geometry
  // stops shifting under a viewer whose token resolves a beat after paint.
  const colCount = 23 + (selectable ? 1 : 0)   // +3: Table, Crown, Pavilion (2026-10-22)
  // The pinned gutter's geometry, in one place, so the header <th>s and the
  // body <td>s cannot drift apart by a pixel (which reads as a torn column).
  const stickyOrdinal = selectable ? 'sticky left-10 z-10 w-11' : 'sticky left-0 z-10 w-11'
  const stickyId = selectable ? 'sticky left-[84px] z-10' : 'sticky left-11 z-10'

  /**
   * The scroller's VISIBLE width, published as a custom property.
   *
   * The expansion row is a `colSpan` cell, so it is as wide as the whole
   * 1280px table — which means that once you have scrolled right to reach the
   * caret, the first several hundred pixels of the panel you just opened are
   * off-screen to the left. (Seen in a real browser: the cut-grade note and
   * the peer explainer both started mid-sentence.) The fix is to pin the
   * panel's CONTENT to the left edge of the viewport and give it that
   * viewport's width — which CSS cannot express on its own, because a sticky
   * box still needs a width. One ResizeObserver, one variable, no per-frame
   * layout read.
   */
  const scrollerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = scrollerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const set = () => el.style.setProperty('--table-viewport', `${el.clientWidth}px`)
    set()
    const ro = new ResizeObserver(set)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div ref={scrollerRef} className="panel overflow-x-auto rounded-lg">
      <table className="min-w-[1280px] w-full text-[12.5px] text-ink tabular">
        {/* Professional B2B header band (2026-10-22, Tej "a pop of professional
            B2B over the minimalism"): the labels sit on a deep-navy bar — the
            same chrome as the site header — so the table reads as a framed data
            surface, not a bare list. A 2px SAPPHIRE underline is the pop of
            colour (the ledger accent), tying the header to the sapphire count +
            badges. Labels are quiet white; the active sort flips to full white
            (never text-ink, which would vanish on navy) and keeps its gold
            caret. Navy-900 is deep in light mode and a raised navy in dark, so
            the band separates from the panel in both themes. Sticky header
            cells carry the same bg so they don't flash paper when scrolled. */}
        <thead className="bg-navy-900 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-white/70 shadow-[inset_0_-2px_0_var(--sapphire)] whitespace-nowrap">
          <tr>
            {selectable && (
              <th className="sticky left-0 z-10 bg-navy-900 px-3 py-3 text-center w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => onToggleAll?.(stones)}
                  className="h-4 w-4 accent-navy align-middle cursor-pointer"
                  aria-label="Select all on this page"
                  data-testid="table-select-all"
                />
              </th>
            )}
            <th className={`${stickyOrdinal} bg-navy-900 px-3 py-3 text-left`}>#</th>
            <th className={`${stickyId} bg-navy-900 px-2 py-3 text-left whitespace-nowrap`}>Stock Id</th>
            <th className="px-2 py-3 text-left">Image</th>
            {/* Supplier = the listing party. KYC-GATED (CLAUDE.md invariant 3),
                but the COLUMN is unconditional now: it used to vanish for an
                anonymous viewer and print an em-dash for an unverified one,
                and a dash reads as "this stone has no supplier". The cell
                carries its own locked state instead — see StoneActions.tsx.
                Location = the stone's ORIGIN, ungated, shown to everyone. #35. */}
            <th className="px-2 py-3 text-left">Supplier</th>
            <th className="px-2 py-3 text-left">Location</th>
            <th className="px-2 py-3 text-left">Shape</th>
            <SortableTh label="Carat" col="carat" sort={sort} onSort={onSort} align="right" />
            <th className="px-2 py-3 text-center">Color</th>
            <th className="px-2 py-3 text-center">Clarity</th>
            {/* Cut / Polish / Symmetry collapse into ONE finish cell: three
                codes over three fill bars. Denser than three columns AND more
                readable, because the bars let you scan quality down the page
                without reading. The header spells the order out so the
                abbreviations are never a guess. */}
            <th className="px-2 py-3 text-center whitespace-nowrap">Cut · Pol · Sym</th>
            <th className="px-2 py-3 text-center">Fluo</th>
            {/* Proportions. Measurements + ratio + depth are derived server-side
                (shared/stoneSpecs.ts). Table %, crown + pavilion angles are now
                published for feed stones (41f1ffd) and shown as columns per Tej
                2026-10-22, with a "—" where the supplier didn't provide the value
                (null across the migrated Aska catalog). Girdle stays unheld. */}
            <th className="px-2 py-3 text-right whitespace-nowrap">Meas. (mm)</th>
            <th className="px-2 py-3 text-right">Ratio</th>
            <th className="px-2 py-3 text-right">Table</th>
            <th className="px-2 py-3 text-right">Depth</th>
            <th className="px-2 py-3 text-right">Crown</th>
            <th className="px-2 py-3 text-right">Pavilion</th>
            <th className="px-2 py-3 text-center">Lab</th>
            <th className="px-2 py-3 text-left">Cert No</th>
            <SortableTh label="$/ct" col="ppc" sort={sort} onSort={onSort} align="right" />
            <SortableTh label="Total" col="price" sort={sort} onSort={onSort} align="right" />
            {/* Save + add-to-cart. Unlabelled on purpose (a header word here
                reads as a data column) — and `relative` for the same reason
                the caret's <th> carries it: `.sr-only` is `position:absolute`
                and would otherwise resolve against the initial containing
                block, 1600px out, dragging horizontal scroll onto the page. */}
            <th className="relative px-2 py-3 w-[84px] text-center"><span className="sr-only">Actions</span></th>
            {/* The expansion caret. Deliberately last and unlabelled — a
                header word here would read as a data column.

                `relative` is load-bearing, not decoration: Tailwind's
                `.sr-only` is `position:absolute`, and inside a 1280px table
                that is itself inside an `overflow-x-auto` panel, an absolutely
                positioned box with no positioned ancestor resolves against the
                INITIAL containing block — i.e. at document x ≈ 1600 — and adds
                165px of horizontal scroll to the whole PAGE. (Measured: 0 →
                165px at a 1440px viewport, before this.) Giving the cell a
                containing block keeps the label inside the scroller where it
                belongs. Same reason `.peer-mark` is positioned in
                stonePeer.css. */}
            <th className="relative px-1 py-3 w-8"><span className="sr-only">Details</span></th>
          </tr>
        </thead>
        {/* Plain tbody — a framer-motion fade (initial opacity 0 → animate 1,
            keyed by bodyKey) used to wrap this, but on a card→table view switch
            a competing re-render re-keyed it mid-animation and it stuck at
            opacity:0 → the whole table rendered invisible (all data present)
            until a reload. Reliability over a decorative fade: the table is now
            always visible. */}
        <tbody key={bodyKey}>
          {stones.map((s, i) => (
            <Row
              key={stoneKey(s)}
              index={i + 1}
              stone={s}
              onOpen={onOpen}
              blurPrice={blurPrice}
              selected={selectedKeys?.has(stoneKey(s))}
              onToggleSelect={onToggleSelect}
              colCount={colCount}
              stickyOrdinal={stickyOrdinal}
              stickyId={stickyId}
              onRequireSignIn={onRequireSignIn}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
})

function SortableTh({
  label, col, sort, onSort, align = 'left',
}: {
  label: string
  col: 'carat' | 'price' | 'ppc'
  sort: Sort
  onSort: (s: Sort) => void
  align?: 'left' | 'right'
}) {
  const active = col === 'carat'
    ? sort === 'carat_asc' || sort === 'carat_desc'
    : col === 'price'
      ? sort === 'price_asc' || sort === 'price_desc'
      : sort === 'ppc_asc' || sort === 'ppc_desc'
  const desc = sort.endsWith('_desc')
  const next: Sort = col === 'carat'
    ? (sort === 'carat_asc' ? 'carat_desc' : 'carat_asc')
    : col === 'price'
      ? (sort === 'price_asc' ? 'price_desc' : 'price_asc')
      : (sort === 'ppc_asc' ? 'ppc_desc' : 'ppc_asc')
  return (
    <th
      className={`px-2 py-3 select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      aria-sort={active ? (desc ? 'descending' : 'ascending') : undefined}
    >
      {/* Real <button> so sorting is keyboard-reachable (Tab + Enter/Space);
          a bare th[onClick] was mouse-only. Inherits the header type style. */}
      <button
        type="button"
        onClick={() => onSort(next)}
        aria-label={`Sort by ${label}${active ? (desc ? ', currently descending' : ', currently ascending') : ''}`}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.08em] font-semibold cursor-pointer transition-colors duration-micro ${align === 'right' ? 'flex-row-reverse' : ''} ${active ? 'text-white' : 'hover:text-white'}`}
      >
        {label}
        {/* Gold caret ONLY on the active sort column (seasoning). */}
        {active && (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-gold-500">
            {desc ? <polyline points="6 9 12 15 18 9" /> : <polyline points="18 15 12 9 6 15" />}
          </svg>
        )}
      </button>
    </th>
  )
}

// Memoized so a parent re-render (e.g. the modal opening/closing on the Search
// page) only reconciles rows whose props actually changed — not all 50 rows +
// their StoneImage subtrees. Props are stable by design: `stone` is a stable
// ref from searchResults, callbacks are useCallback'd in Search, and only the
// toggled row's `selected` boolean flips. Default shallow compare suffices.
// (The peer bands arrive by context, deliberately: a new required prop would
// change identity on every parent render and undo exactly this.)
const Row = memo(function Row({
  index, stone, onOpen, blurPrice, selected, onToggleSelect, colCount, stickyOrdinal, stickyId, onRequireSignIn,
}: {
  index: number
  stone: Diamond
  onOpen?: (s: Diamond) => void
  blurPrice?: boolean
  selected?: boolean
  onToggleSelect?: (s: Diamond) => void
  colCount: number
  stickyOrdinal: string
  stickyId: string
  onRequireSignIn?: () => void
}) {
  const mult = 1
  const [expanded, setExpanded] = useState(false)
  // ONE rule for the market position on this row: it exists only when the
  // price it annotates is actually visible. The server already withholds the
  // bands from anyone who may not read prices; this is the belt-and-braces
  // half, and it covers the expansion as well as the cell, so the two can
  // never disagree about whether a position may be shown.
  const rawPeer = usePeer(stone, mult)
  const peer = !blurPrice && stone.pricePerCarat != null ? rawPeer : null
  const detailId = `row-detail-${stoneKey(stone)}`
  // The pinned gutter has to carry the row's own background or the scrolling
  // columns slide visibly underneath it.
  const pinBg = selected ? 'bg-gold-050' : 'bg-paper group-hover:bg-gold-050'

  // Desktop finally gets a tactile answer to a press: `feedback` used to be
  // quarantined under web/src/mobile/, so every click on this shell was silent.
  const toggle = useCallback(() => {
    feedback.select()
    setExpanded((v) => !v)
  }, [])

  return (
    <>
      <tr
        // `row-actions-host` is the hover/focus scope the recessive row
        // actions key off (stoneSpecs.css) — a class rather than `tr:hover` so
        // the identical rule serves the mobile row, which has no <tr>.
        className={`group row-actions-host border-t border-line cursor-pointer transition-colors duration-micro
                   border-l-2 hover:bg-gold-050 hover:border-l-gold-500
                   ${selected ? 'bg-gold-050 border-l-gold-500' : 'border-l-transparent'}`}
        onClick={() => onOpen?.(stone)}
        // ── THE ROW IS A CONTROL, SO IT IS IN THE TAB ORDER ─────────────────
        // It carries `onClick` (open this stone) and `aria-expanded`, and it
        // had neither `tabIndex` nor an Enter/Space handler: the click was
        // mouse-only, and the ArrowRight/ArrowLeft expand below could only
        // ever fire INCIDENTALLY, when a keydown happened to bubble up from a
        // descendant that WAS focusable (the select checkbox, the caret). A
        // keyboard user could not open a stone from this table at all.
        tabIndex={0}
        // ArrowRight/ArrowLeft expand and collapse without leaving the row —
        // the keyboard equivalent of the caret, and the only way a keyboard
        // user reaches the spec sheet without opening (and then dismissing)
        // the full modal, which costs their place in the list.
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' && !expanded) { e.preventDefault(); toggle() }
          else if ((e.key === 'ArrowLeft' || e.key === 'Escape') && expanded) { e.preventDefault(); toggle() }
          // Enter/Space only from the row ITSELF: a checkbox, the cart button
          // and the caret all live inside it and own those keys already.
          else if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault(); onOpen?.(stone)
          }
        }}
        aria-expanded={expanded}
        data-testid="diamond-table-row"
        data-stock-id={stone.stockId}
      >
        {onToggleSelect && (
          <td className={`sticky left-0 z-10 px-3 py-2.5 text-center transition-colors duration-micro ${pinBg}`} onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggleSelect(stone)}
              className="h-4 w-4 accent-navy align-middle cursor-pointer"
              aria-label={`Select #${displayStockId(stone)} to share`}
              data-testid="table-row-select"
            />
          </td>
        )}
        <td className={`${stickyOrdinal} px-3 py-2.5 text-navy-300 transition-colors duration-micro ${pinBg}`}>{index}</td>
        {/* Stock id moved UP beside the ordinal and into the pinned gutter:
            the identity has to survive a horizontal scroll to $/ct, or the
            whole right-hand half of the table is anonymous numbers. */}
        {/* whitespace-nowrap is load-bearing: in the pinned gutter the browser
            gives this column its min-content width, and a hyphenated supplier
            id ("#VL-158201") wrapped to two lines and grew every row by 17px. */}
        <td className={`${stickyId} px-2 py-2.5 mono-id font-semibold text-ink whitespace-nowrap transition-colors duration-micro ${pinBg}`}>#{displayStockId(stone)}</td>
        <td className="px-2 py-2">
          {/* Squarer + slightly larger than the old 36px circle: a round crop cuts
              the corners off every fancy shape, which is exactly the information
              a thumbnail in a stone list exists to carry. rounded-md is the v2
              card radius, so it matches the rest of the surface. */}
          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-md ring-1 ring-line bg-gradient-to-br from-cream to-[#eef1f6]">
            <StoneImage stone={stone} badge={false} width={128} />
          </div>
        </td>
        {/* KYC-gated. The cell renders for every viewer; what it SAYS is
            decided purely by whether the server sent a supplier name. */}
        <td className="px-2 py-2.5 max-w-[170px] text-ink">
          <SupplierName stone={stone} signedOut={!!blurPrice} compact />
        </td>
        <td className="px-2 py-2.5 truncate max-w-[180px] text-navy-500" title={stone.origin || ''}>{stone.origin || '—'}</td>
        <td className="px-2 py-2.5">{stone.shape || '—'}</td>
        <td className="px-2 py-2.5 text-right font-mono tabular-nums">{formatCarat(stone.carat)}</td>
        <td className="px-2 py-2.5 text-center">{stone.color || '—'}</td>
        <td className="px-2 py-2.5 text-center">{stone.clarity || '—'}</td>
        <td className="px-2 py-2 text-center">
          <FinishStrip stone={stone} className="justify-center" />
        </td>
        <td className="px-2 py-2.5 text-center text-navy-500">{stone.fluorescence || '—'}</td>
        <td className="px-2 py-2.5 text-right tabular-nums text-navy-500 whitespace-nowrap">
          {formatMeasurements(stone)?.replace(' mm', '') || '—'}
        </td>
        <td className="px-2 py-2.5 text-right tabular-nums text-navy-500">
          {stone.specs?.ratio != null ? stone.specs.ratio.toFixed(2) : '—'}
        </td>
        <td className="px-2 py-2.5 text-right tabular-nums text-navy-500">
          {stone.specs?.tablePct != null ? `${stone.specs.tablePct.toFixed(1)}%` : '—'}
        </td>
        <td className="px-2 py-2.5 text-right tabular-nums text-navy-500">
          {stone.specs?.depthPct != null ? `${stone.specs.depthPct.toFixed(1)}%` : '—'}
        </td>
        <td className="px-2 py-2.5 text-right tabular-nums text-navy-500">
          {stone.specs?.crownAngle != null ? `${stone.specs.crownAngle.toFixed(1)}°` : '—'}
        </td>
        <td className="px-2 py-2.5 text-right tabular-nums text-navy-500">
          {stone.specs?.pavilionAngle != null ? `${stone.specs.pavilionAngle.toFixed(1)}°` : '—'}
        </td>
        {/* Ledger: blue = trust — the grading lab is the cert authority. */}
        <td className="px-2 py-2.5 text-center font-mono font-semibold text-sapphire">{stone.lab || '—'}</td>
        <td className="px-2 py-2.5 mono-id truncate max-w-[140px] text-navy-500" title={stone.certNo || ''}>
          <CertLink lab={stone.lab} certNo={stone.certNo} />
        </td>
        {/* $/ct carries the market position as an ANNOTATION of the number,
            inside the existing cell. Zero new columns — this table is already
            1280px wide, and the mark is a statement about this price, not a
            separate fact. */}
        <td className="px-2 py-2.5 text-right tabular-nums text-navy-500">
          <span className="inline-flex flex-col items-end gap-0.5">
            {/* Ledger: $/ct is THE value the trade compares on → gold + mono. */}
            <span className="font-mono font-semibold text-gold-600">{blurPrice || stone.pricePerCarat == null ? '—' : formatPerCarat(Math.round(stone.pricePerCarat * mult))}</span>
            {/* A market position sitting next to a "Sign in" cell would be
                both nonsense and a hint about the number we are deliberately
                withholding — `peer` above is already null in that case. */}
            <PriceMark stone={stone} peer={peer} variant="row" index={index - 1} />
          </span>
        </td>
        <td className="px-2 py-2.5 text-right font-mono font-semibold tabular-nums text-ink">
          {blurPrice
            ? <span className="text-gold-600 font-semibold">Sign in</span>
            : stone.priceUSD == null
              ? <span className="inline-flex items-center gap-1 text-navy-500 font-semibold" data-testid="price-locked" title="Prices unlock once your account is verified">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                  Verifying
                </span>
              : formatPrice(Math.round(stone.priceUSD * mult))}
        </td>
        {/* Save. Always offered — anonymously it routes to sign-in rather than
            401-ing silently. */}
        <td className="px-2 py-2 w-[84px]" onClick={(e) => e.stopPropagation()}>
          <span className="inline-flex items-center justify-center gap-2">
            <SaveStoneButton
              stone={stone}
              signedOut={!!blurPrice}
              onRequireSignIn={onRequireSignIn}
              className="h-8 w-8"
            />
          </span>
        </td>
        <td className="px-1 py-2 w-8" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={toggle}
            aria-expanded={expanded}
            aria-controls={detailId}
            aria-label={expanded ? `Hide full specification for ${displayStockId(stone)}` : `Show full specification for ${displayStockId(stone)}`}
            data-testid="table-row-expand"
            // 32px is the VISUAL size; `.tap-44` supplies the 44px hit area.
            // This shell renders on every viewport >= 768px, which includes an
            // iPad in Safari — "desktop" here is not a promise of a mouse.
            className="tap-44 inline-flex h-8 w-8 items-center justify-center rounded-full text-navy-300 hover:bg-navy-100/40 hover:text-ink transition-colors duration-micro"
          >
            <svg
              width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden
              className={`transition-transform duration-default ease-premium ${expanded ? 'rotate-90' : ''}`}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </td>
      </tr>
      {expanded && (
        /* A DIFFERENT testid on purpose: `diamond-table-row` is counted by
           DiamondTable.test.tsx and read by scripts/qa/browser-accounts.mjs.
           An expansion that answered to the same name would make a row count
           depend on which rows happen to be open. */
        <tr data-testid="diamond-table-row-detail" className="border-t border-line bg-cream/50">
          <td id={detailId} colSpan={colCount} className="p-0">
            {/* Pinned to the scroller's left edge and sized to its visible
                width (see --table-viewport above), so the detail reads in full
                no matter how far right the row was scrolled when it opened. */}
            <div className="detail-rise sticky left-0 px-4 py-4" style={{ width: 'var(--table-viewport, 100%)' }}>
              <RowDetail stone={stone} peer={peer} onOpen={onOpen} />
            </div>
          </td>
        </tr>
      )}
    </>
  )
})

/**
 * The in-place expansion — parity with the mobile row's tap-and-hold, which
 * shipped this first. It exists because the only other way to see the rest of
 * a stone was a full modal, and dismissing a modal costs you your scroll
 * position in a 27,000-stone list.
 *
 * ── WHY THE 360 PLAYER IS NOT IN HERE ─────────────────────────────────────
 * It was, and it was wrong. `Diaex360` is a hero component: at 112px its own
 * chrome — the pause control, the fullscreen button and the "tap to pause,
 * drag to spin" hint — covered the stone completely, and each expansion fired
 * nine frame requests through /api/v. (Both measured in a real browser, not
 * assumed.) A row expansion is a glance, not a viewing. So the panel shows the
 * real photo at a size you can judge, flags that a 360 exists, and hands you
 * one press into the modal where the player has the room it was built for.
 */
function RowDetail({
  stone, peer, onOpen,
}: {
  stone: Diamond
  peer: ReturnType<typeof usePeer>
  onOpen?: (s: Diamond) => void
}) {
  const hasSpin = !!stone.media?.videoUrl

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
      <button
        type="button"
        onClick={() => onOpen?.(stone)}
        data-testid="row-detail-media"
        aria-label={hasSpin ? `View ${displayStockId(stone)} in 360°` : `View ${displayStockId(stone)}`}
        className="group/med relative h-28 w-28 shrink-0 overflow-hidden rounded-md ring-1 ring-line bg-gradient-to-br from-cream to-[#eef1f6] transition-shadow duration-default ease-premium hover:shadow-elev-2"
      >
        <StoneImage stone={stone} badge={false} width={256} />
        {hasSpin && (
          <span className="absolute bottom-1 right-1 inline-flex items-center gap-1 rounded-full bg-paper/95 px-1.5 py-0.5 text-[9px] font-bold text-ink shadow-elev-1">
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 2v6M12 16v6M2 12h6M16 12h6" /><circle cx="12" cy="12" r="3.5" />
            </svg>
            360°
          </span>
        )}
      </button>
      <div className="min-w-0 flex-1 space-y-3">
        <SpecSheet stone={stone} />
        <PeerExplainer peer={peer} />
      </div>
    </div>
  )
}
